import { useFlags } from 'launchdarkly-react-client-sdk';
import { useFormContext } from 'react-hook-form';
import { fields } from '../constants';
import { useMemo, useState } from 'react';
import { useGetAllowedTargeting } from '@apis/allowedTargeting';
import { getTargetingValue } from '../../utils';
import { useDomain } from '@hooks/domain';
import { useModal } from '@hooks/modal';
import type { Bundle } from '@local-types/bundle';
import { Fields } from '@local-types';
import {
  AdvancedInventoryData,
  InventoryTargetingStoreStructure,
} from '@local-types/inventory-targeting';
import { IncludableData } from '@v2/components/campaign/Advanced/types';
import { UnsavedChanges } from '@components/Wizard';

export const useInventory = ({
  bundles,
  isDisplay,
}: {
  bundles: Bundle[];
  isDisplay: boolean;
}) => {
  const { releaseDragAdvancedTargeting } = useFlags();
  const { setValue, control, watch } =
    useFormContext<Fields<typeof fields>>();
  const { data } = useGetAllowedTargeting();
  const domain = useDomain();
  const { setModal } = useModal();
  const [isCustomInventoryExpanded, setIsCustomInventoryExpanded] =
    useState(false);

  const [customInventoryItems, advancedInventory] = watch([
    fields.advancedCustomInventory.path,
    fields.advancedInventory.path,
  ]);

  const actualAdvancedInventory = useMemo(() => {
    const { os, device } = (advancedInventory ?? {
      os: {},
      device: {},
    }) as AdvancedInventoryData;

    return [
      ...os.ids.map(key => ({
        ...getTargetingValue(key, data),
        included: !os.blacklist,
      })),
      ...device.ids.map(key => ({
        ...getTargetingValue(key, data),
        included: !device.blacklist,
      })),
    ] as IncludableData<InventoryTargetingStoreStructure>[];
  }, [advancedInventory, data]);

  const selectRecommendedInventory = () => {
    if (domain.peacock) {
      const singlePeacockBundle = bundles.filter(
        item => item.bundle_name == 'Run Of Network',
      )[0];
      setValue(fields.advancedCustomInventory.path, [singlePeacockBundle]);
    } else {
      const recommendedBundles = bundles.filter(
        ({ recommended }) => recommended,
      );
      setValue(fields.advancedCustomInventory.path, recommendedBundles);
    }
  };

  const clearCustomInventory = async () => {
    setValue(fields.advancedCustomInventory.path, []);
  };

  const confirmationMessage = (
    <>
      Are you sure that you want to switch to a preset inventory? <br />
      Selected custom inventory will be cleared.
    </>
  );

  const confirmSwitchingToInventoryPreset = handler => {
    setModal({
      isOpen: true,
      component: () => (
        <UnsavedChanges
          onCancel={() => setModal(null)}
          confirmationHeader={'Custom inventory reset'}
          confirmationMessage={confirmationMessage}
          onConfirm={async () => {
            await clearCustomInventory();
            await handler();
            setModal(null);
          }}
        />
      ),
    });
  };

  return {
    confirmSwitchingToInventoryPreset,
    customInventoryItems,
    setValue,
    control,
    domain,
    isCustomInventoryExpanded,
    setIsCustomInventoryExpanded: () => setIsCustomInventoryExpanded(true),
    inventory: actualAdvancedInventory,
    selectRecommendedInventory,
    showAdvancedTargeting: !isDisplay || releaseDragAdvancedTargeting,
  };
};
